// Cloud-server API client. Mirrors the demo HTML harness's interactions
// (preview → SSE → patterns → convert → SSE → encrypted viewer bundle),
// adapted for a real cross-origin React surface.

import { resolveAdClick, type AdClickKind } from "./adClick";
import type { WizardEventPayload } from "./wizard-events";

// Ad-click attribution fields shared by the three server routes that
// accept them (/apply, /self-serve/start, /billing/checkout). Resolved
// from the marketing-site cookie / URL param — see src/lib/adClick.ts.
export function adClickFields(): {
  // The narrow union, not `string`: ApplyBody types this field as the three
  // known kinds, so widening here would force a cast at the apply call site
  // (and did — tsc caught it the moment the three payloads were unified).
  ad_click_kind: AdClickKind | null;
  ad_click_id: string | null;
  ad_landed_at: string | null;
  ad_click_src: string | null;
} {
  const ad = resolveAdClick();
  return {
    ad_click_kind: ad?.kind ?? null,
    ad_click_id: ad?.id ?? null,
    ad_landed_at: ad?.landed_at ?? null,
    ad_click_src: ad?.src ?? null,
  };
}

export interface Pattern {
  pattern_id: string;
  rationale?: string;
  confidence?: number | string;
  kind?: string;
  instances_observed?: string;
  code?: string;
}

export interface PreviewSummary {
  pattern_count: number;
  estimated_remaining_cost_usd?: number | null;
  patterns?: Pattern[] | null;
}

export interface StatusResponse {
  cloud_run_id: string;
  chain_kind: "preview" | "full" | "convert";
  chain_state: string;
  chain_meta?: Record<string, unknown> | null;
  started_at?: string;
  exited_at?: string | null;
  exit_code?: number | null;
  client_id_masked?: string;
  // Operator-only owner attribution (run -> code -> application), populated only
  // by /operator/runs/{id}/status. Absent/null on the token-authed user status.
  owner_email?: string | null;
  owner_name?: string | null;
  // v16, operator status only: the run's notify-email override, for when
  // owner_email/owner_name resolve nothing (a self-serve run with no
  // application behind its access code). Same operator-only gating.
  notify_email?: string | null;
  // Journey links (operator status only, 2026-07-02): a consumed preview
  // points at the analysis run it fed; an analysis run reports the preview
  // run(s) that fed it + their rolled-up cost, so the detail reads
  // journey-wide (same pairing as the folded list).
  successor_run_id?: string | null;
  journey_preview_run_ids?: string[] | null;
  journey_preview_cost_usd?: number | null;
  // While chain_state === "queued": queued runs ahead of this one (0 = next up),
  // recomputed each poll so it ticks down. Null/absent once the run starts.
  queue_position?: number | null;
  preview_summary?: PreviewSummary | null;
  // Submit-time passphrase, re-served while the run is in flight so a
  // reloaded tab can re-show the save-it card. Never present once terminal.
  pending_passphrase?: string | null;
}

export interface PreviewResponse {
  preview_run_id: string;
  preview_token: string;
  started_at?: string;
  // Submit-time viewer passphrase, minted at the FIRST submit so the
  // hand-off shows it immediately (audit B7 follow-up). The server carries
  // the same key to the full run at promotion; /runs/start echoes it.
  passphrase?: string | null;
}

export interface StartResponse {
  run_id: string;
  run_token: string;
  preview_consumed?: boolean;
  started_at?: string;
  // "queued" when all full-run slots were busy and the run was accepted into the
  // queue (the scheduler starts it when a slot frees); otherwise a live state
  // ("running"/"initializing"). Drives the queued hand-off screen.
  state?: string;
  // Queued runs ahead of this one at enqueue (0 = next up). Null when started now.
  queue_position?: number | null;
  // Submit-time viewer passphrase (audit B7): delivered exactly once, here.
  // The server discards its copy when the bundle is encrypted at completion,
  // so the hand-off screen must get the user to save it.
  passphrase?: string | null;
}

export interface ApiError extends Error {
  status: number;
  code?: string;
  detail?: string;
  // Seconds until the server-side condition clears (daily cap resets at
  // midnight UTC, rate-limit windows, etc). Present on the ErrorResponse
  // model's `retry_after` field; undefined when the server didn't send one.
  retryAfter?: number;
}

/** True for the server's hard agreement gate on /runs/start (runs.py):
 * HTTP 403 with detail "agreement_not_accepted". The caller sends the user
 * back through the Welcome agreement rather than showing a generic run error. */
export function isAgreementGateError(e: unknown): boolean {
  const ae = e as ApiError | null;
  return !!ae && ae.status === 403 && ae.detail === "agreement_not_accepted";
}

function makeApiError(status: number, body: unknown, fallback: string): ApiError {
  const e = new Error() as ApiError;
  e.status = status;
  if (body && typeof body === "object") {
    const b = body as { code?: string; detail?: unknown; retry_after?: unknown };
    e.code = b.code;
    if (typeof b.retry_after === "number") e.retryAfter = b.retry_after;
    // FastAPI's `detail` is a string for our own HTTPExceptions, but a LIST
    // of {type, loc, msg, input} objects for 422 validation errors. message
    // must always end up a string — a non-string here once reached a React
    // render as-is and took down the whole operator panel (React #31).
    if (typeof b.detail === "string") {
      e.detail = b.detail;
    } else if (Array.isArray(b.detail)) {
      e.detail = b.detail
        .map((d) =>
          d && typeof d === "object" && "msg" in d
            ? String((d as { msg: unknown }).msg)
            : JSON.stringify(d),
        )
        .join("; ");
    } else if (b.detail !== undefined && b.detail !== null) {
      e.detail = JSON.stringify(b.detail);
    }
  }
  e.message = e.detail || e.code || fallback;
  return e;
}

function serverUrl(server: string, path: string): string {
  return `${server.replace(/\/$/, "")}${path}`;
}

function authHeaders(
  clientId: string,
  accessCode?: string | null,
): Headers {
  const h = new Headers({
    "X-Aurora-Client": clientId,
  });
  // Aurora-paid endpoints (/runs/preview, /runs/start) require an
  // access code. The cloud-frontend
  // Landing flow stamps the code into localStorage; pass it through
  // here so launch calls are gated end-to-end.
  if (accessCode) h.set("X-Access-Code", accessCode);
  return h;
}


// PWYC price ladder returned by POST /prescan (pricing decision
// 2026-06-30): one flat pay-what-you-can ladder — Solidarity / Standard /
// Access — identical for every corpus size, self-selected on the cost
// screen. price_usd is the EFFECTIVE (operator-overridable) price the
// checkout will charge. blurb is the self-placement line under the name.
export interface PwycTier {
  name: "Solidarity" | "Standard" | "Access";
  price_usd: number;
  blurb: string;
  // Per-tier cost viability (door 2, 2026-08-05): false → this record's run
  // cost exceeds the tier's server-side ceiling (Access has its own, lower
  // ceiling than Standard/Solidarity) and the card renders disabled — visible
  // but not selectable. Omitted by a server that predates the field → treat
  // as viable (`!== false`), so a mixed-deploy window can't brick the screen.
  viable?: boolean;
}

export interface PrescanResponse {
  // Corpus is over the self-serve boundary → the cost screen routes to the
  // Done-for-you Service ("write us") instead of showing a price ladder.
  service_route: boolean;
  pwyc_tiers: PwycTier[];
  token_estimate: number;
  message_count: number;
  pages_estimate: number;
  // Real PDF page count from the source export. Null for non-PDF
  // sources (Civil Communicator txt, generic CSV); the wizard renders
  // the token-derived `pages_estimate` with a "roughly" hedge in that
  // case, and the verifiable count without the hedge when this is set.
  actual_pages: number | null;
  // Per-run cost estimate (USD) + whether it exceeds the server's per-run
  // budget ceiling. When `over_budget` is true the record is too large to run
  // within the pilot's per-run budget; the cost screen shows the off-ramp
  // (email us) instead of letting the user authorize a run that would abort.
  // Omitted by a server that predates this field → read `=== true`.
  estimate_usd?: number | null;
  over_budget?: boolean;
  budget_usd?: number | null;
  // Server-controlled pilot flag. When true the cost screen presents
  // the run as free-during-pilot (a grant covers it). A Service-routed
  // record is never covered — the wizard keeps it on the "write us" path
  // regardless. An older server that predates this field omits it;
  // consumers read it with `=== true` so undefined reads as normal pricing.
  pilot_free: boolean;
  // Whether public self-serve checkout can actually start right now (the same
  // compound gate /billing/checkout enforces). A paid run whose checkout is
  // CLOSED shows a graceful "request access" state instead of an Authorize
  // button that would 404. An older server omits it → read with `=== true`
  // (undefined = closed / fail-closed).
  self_serve_open?: boolean;
  // Whether this access code already has a completed Stripe payment on file
  // (launch-audit fix, 2026-07-04) — a normal state in a FRESH browser
  // session: a cancelled/failed checkout re-arms the code while the payment
  // record survives (never double-charge), so a paid-but-unused code recurs.
  // Independent of `paidThisVisit` (App's same-visit-only flag, set right
  // after a Stripe redirect) — this is the server's own record, so it also
  // covers the case where the payment predates this browser session
  // entirely. When true, the cost screen shows an "already paid" band
  // instead of the PWYC cards. An older server omits it → read `=== true`.
  already_paid?: boolean;
  // The amount (USD) and PWYC tier name actually paid, present when
  // already_paid is true (null/absent when the payment record predates these
  // fields, or already_paid is false) — the already-paid band's copy uses
  // the amount and degrades gracefully when it's missing.
  paid_amount_usd?: number | null;
  paid_tier?: string | null;
}


// One default run model for the whole client. The cost screen prices this model
// (threaded to /prescan) and the run uses it (sent to /runs/*), so the
// over-budget off-ramp and the chain's pre-flight gate price the same thing.
// Change it here and every call site moves together.
export const DEFAULT_MODEL_VERSION = "claude-opus-4-8";

export async function postPrescan(args: {
  server: string;
  corpusFile: File;
  accessCode: string;
  promptVars?: Record<string, unknown>;
  // Same model the run will use, so the estimate prices what the chain gates on.
  modelVersion?: string;
  /**
   * Page count of the SOURCE PDF, when the corpus came from one.
   *
   * The server prefers this over its token-derived `pages_estimate`, and the
   * cost screen then drops the "roughly" hedge for a number the user can check
   * against their own export. `prescan_routes` has read
   * `meta.ingestion.page_count` since the OFW adapter landed, but nothing ever
   * sent it — so the precise branch had never rendered in the cloud wizard for
   * any adapter (found 2026-07-30 on a 197-page AppClose export displayed as
   * "roughly 63 pages"). Omitted for CSV sources, which have no pages and must
   * keep the hedge.
   */
  sourcePageCount?: number;
  // Lets the caller time-box the size check (a hung /prescan must not strand the
  // wizard — the caller fails open on abort and the chain gate is the backstop).
  signal?: AbortSignal;
}): Promise<PrescanResponse> {
  const fd = new FormData();
  fd.append("corpus_csv", args.corpusFile);
  fd.append(
    "meta_json",
    JSON.stringify({
      access_code: args.accessCode,
      prompt_vars: args.promptVars ?? {},
      model_version: args.modelVersion ?? DEFAULT_MODEL_VERSION,
      // The server ignores a missing/zero/non-positive value and falls back to
      // pages_estimate, so send the key only when we actually measured it.
      ...(typeof args.sourcePageCount === "number" && args.sourcePageCount > 0
        ? { ingestion: { page_count: args.sourcePageCount } }
        : {}),
    }),
  );
  const r = await fetch(serverUrl(args.server, "/prescan"), {
    method: "POST",
    body: fd,
    signal: args.signal,
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "cost estimate failed",
    );
  }
  // Validate the shape before handing it to the cost screen. A 200 with a
  // malformed body (missing pwyc_tiers) would otherwise reach PwycCostPanel
  // as a ladder-less estimate and crash the whole wizard (no error boundary).
  // Treat it as a retryable error instead, so the user sees the cost-screen
  // retry, not a blank page.
  const body: unknown = await r.json();
  const est = body as Partial<PrescanResponse> | null;
  // Check the fields the cost screen actually dereferences: the PWYC ladder
  // must be a non-empty array of named tiers, and service_route must be
  // present (an old-model server without it would render a price ladder the
  // checkout can't honor).
  if (
    est == null ||
    typeof est !== "object" ||
    typeof est.service_route !== "boolean" ||
    !Array.isArray(est.pwyc_tiers) ||
    est.pwyc_tiers.length === 0 ||
    est.pwyc_tiers.some(
      (t) => typeof (t as { name?: unknown })?.name !== "string",
    )
  ) {
    throw makeApiError(r.status, body, "cost estimate returned an unexpected response");
  }
  return est as PrescanResponse;
}


export interface FeedbackBody {
  body: string;
  email?: string | null;
}

export async function submitFeedback(args: {
  server: string;
  accessCode: string;
  feedback: FeedbackBody;
}): Promise<void> {
  const r = await fetch(serverUrl(args.server, "/feedback"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Access-Code": args.accessCode,
    },
    body: JSON.stringify(args.feedback),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "feedback submission failed",
    );
  }
}

export async function checkHealth(server: string): Promise<{
  ok: boolean;
  version: string;
  result_retention_days: number;
  fanout_batched: boolean;
  ready_email_enabled: boolean;
  anthropic_service_degraded: boolean;
}> {
  const r = await fetch(serverUrl(server, "/healthz"));
  if (!r.ok) throw makeApiError(r.status, await r.json().catch(() => ({})), "healthz failed");
  const body = await r.json();
  return {
    ok: !!body.ok,
    version: body.version ?? "",
    // The retention window the result stays re-downloadable (the server's
    // run_complete TTL). Absent field (older server) defaults to 7.
    result_retention_days: body.result_retention_days ?? 7,
    // Whether full runs are batched (cloud default). Drives the run-screen
    // hand-off vs the live "keep this tab open" view. A server that doesn't
    // report the field is the OLD synchronous server → default false (live view).
    fanout_batched: body.fanout_batched === true,
    // S13: the hand-off's "we'll email you" promise renders only when the
    // server says it will actually send. Absent field (older server) = OFF.
    ready_email_enabled: body.ready_email_enabled === true,
    // Operator-set provider-outage signal → drives the degraded banner. Absent
    // field (older server) = OFF (no banner).
    anthropic_service_degraded: body.anthropic_service_degraded === true,
  };
}

export async function postPreview(args: {
  server: string;
  clientId: string;
  accessCode: string;
  corpusFile: File;
  metaJson: string;
  adapter: string;
  promptVersion?: string;
  modelVersion?: string;
}): Promise<PreviewResponse> {
  const fd = new FormData();
  fd.append("corpus_csv", args.corpusFile);
  fd.append("meta_json", args.metaJson);
  fd.append("adapter", args.adapter);
  // Advisory only since 2026-07-22: the server stamps the deployed engine's
  // own CHAIN_PROMPT_VERSION and ignores this except as a last-resort
  // fallback (the old hardcoded "v14" here had drifted three versions
  // behind the engine and was being recorded as fact).
  fd.append("prompt_version", args.promptVersion ?? "");
  fd.append("model_version", args.modelVersion ?? DEFAULT_MODEL_VERSION);

  const r = await fetch(serverUrl(args.server, "/runs/preview"), {
    method: "POST",
    headers: authHeaders(args.clientId, args.accessCode),
    body: fd,
  });
  if (!r.ok) throw makeApiError(r.status, await r.json().catch(() => ({})), "preview failed");
  return r.json();
}

// Fire-and-forget beacon from the Checkout-return boot (2026-07-22, Lisa Garg
// stranding): records server-side WHICH branch the return took (paid-launch /
// paid-reload / stash-mismatch / ...), so the next stranded-buyer diagnosis
// starts from a logged fact instead of inference-from-silence. Diagnostics
// only — never throws, callers never await the outcome.
export async function postCheckoutReturn(args: {
  server: string;
  kind: string;
  accessCode: string | null;
}): Promise<void> {
  try {
    await fetch(serverUrl(args.server, "/billing/checkout-return"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kind: args.kind,
        access_code: args.accessCode || null,
      }),
      keepalive: true,
    });
  } catch {
    // beacon only — swallow everything
  }
}

// Fire-and-forget event from the wizard's pre-run steps (2026-07-30): the
// corpus is parsed in-browser, so anything that goes wrong before /prescan
// used to reach the server as nothing at all. Metadata only — see
// lib/wizard-events.ts for what may travel and why the filename may not.
// Never throws; callers never await the outcome.
export async function postWizardEvent(args: {
  server: string;
  accessCode: string | null;
  payload: WizardEventPayload;
}): Promise<void> {
  try {
    await fetch(serverUrl(args.server, "/ingest/wizard-event"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(args.accessCode ? { "X-Access-Code": args.accessCode } : {}),
      },
      body: JSON.stringify(args.payload),
      // The user may click away from a dead-ended import immediately.
      keepalive: true,
    });
  } catch {
    // beacon only — swallow everything
  }
}

// The same event, sent from a tab that is going away. `fetch` — even with
// keepalive — is not reliable during pagehide; sendBeacon is the one the
// browser promises to flush. It cannot set headers, so the access code
// rides in the body (the route accepts either). Returns whether the browser
// accepted it, for tests.
//
// The content type is load-bearing, and NOT application/json. In production
// the SPA runs on cloud.plaintosee.cc and the API on app.plaintosee.cc — a
// cross-origin pair. `application/json` is not a CORS-safelisted request
// type, so it forces a preflight, and a preflight started during pagehide
// does not finish: the page is gone before the OPTIONS returns and the
// beacon dies with it. `text/plain` IS safelisted, so the POST goes as a
// simple request with no preflight and actually lands. (The route parses
// the body itself rather than trusting the header for this reason.)
// This is invisible in local dev, where the wizard is same-origin behind the
// Vite proxy and every content type works.
export function sendWizardEventOnUnload(args: {
  server: string;
  accessCode: string | null;
  payload: WizardEventPayload;
}): boolean {
  try {
    const body = new Blob(
      [JSON.stringify({ ...args.payload, access_code: args.accessCode })],
      { type: "text/plain;charset=UTF-8" },
    );
    return navigator.sendBeacon(
      serverUrl(args.server, "/ingest/wizard-event"),
      body,
    );
  } catch {
    return false;
  }
}

export async function postConvert(args: {
  server: string;
  clientId: string;
  accessCode: string;
  corpusFile: File;
  metaJson: string;
  adapter: string;
  previewRunId: string;
  previewToken: string;
  promptVersion?: string;
  modelVersion?: string;
  // Belt-and-braces for a local-stop race (finding 3a, launch-audit review
  // 2026-07-04): wired to the caller's cancel affordance so a Cancel click
  // while this corpus re-upload is still in flight aborts the network
  // request itself, not just the UI. Optional — callers that don't offer a
  // mid-flight cancel simply never abort.
  signal?: AbortSignal;
}): Promise<StartResponse> {
  const fd = new FormData();
  fd.append("corpus_csv", args.corpusFile);
  fd.append("meta_json", args.metaJson);
  fd.append("adapter", args.adapter);
  // Advisory only since 2026-07-22: the server stamps the deployed engine's
  // own CHAIN_PROMPT_VERSION and ignores this except as a last-resort
  // fallback (the old hardcoded "v14" here had drifted three versions
  // behind the engine and was being recorded as fact).
  fd.append("prompt_version", args.promptVersion ?? "");
  fd.append("model_version", args.modelVersion ?? DEFAULT_MODEL_VERSION);
  fd.append("preview_run_id", args.previewRunId);
  fd.append("preview_token", args.previewToken);
  // Reader's IANA timezone → the "ready" email renders the start time in their
  // local clock + abbreviation instead of UTC. Best-effort; server falls back.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) fd.append("timezone", tz);
  } catch {
    /* ancient browser without Intl tz → server keeps UTC */
  }

  const r = await fetch(serverUrl(args.server, "/runs/start"), {
    method: "POST",
    headers: authHeaders(args.clientId, args.accessCode),
    body: fd,
    signal: args.signal,
  });
  if (!r.ok) throw makeApiError(r.status, await r.json().catch(() => ({})), "convert failed");
  return r.json();
}

// Override (or clear, with email="") the address the "analysis ready" + expiry-
// reminder emails go to for this run — the user changed it on the run hand-off screen.
// Authed by the run token (the credential that reopens the run). The server
// validates the address; returns the address now on file (null = cleared).
export async function setNotifyEmail(args: {
  server: string;
  runId: string;
  runToken: string;
  email: string;
}): Promise<{ ok: boolean; email: string | null }> {
  const r = await fetch(serverUrl(args.server, "/runs/notify-email"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ run_id: args.runId, token: args.runToken, email: args.email }),
  });
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "couldn't update the email");
  }
  const b = await r.json();
  return { ok: !!b.ok, email: b.email ?? null };
}

// --- Entity classifier ---

export interface ChildMention {
  name: string;
  mention_count: number;
  evidence_quote?: string | null;
}

export interface OtherNameMention {
  name: string;
  mention_count: number;
}

/** A nickname the classifier matched to a canonical party/child, with how sure
 *  it is. Mirrors the server's NicknameMentionSchema. The editor folds these in
 *  as "also called" aliases (high-confidence) and derives merge suggestions
 *  (when an alias is also a separately-detected child). */
export interface NicknameMention {
  alias: string;
  canonical: string;
  evidence_quote: string;
  confidence: "high" | "medium" | "low";
  mention_count: number;
}

export interface ClassifyEntitiesResponse {
  party_a: string;
  party_b: string;
  children: ChildMention[];
  other_names: OtherNameMention[];
  /** Nickname candidates (alias → canonical) the classifier matched. The server
   *  has returned these since the Haiku nickname-suggestion feature; the cloud
   *  wizard now folds them into the people editor instead of dropping them. */
  nicknames: NicknameMention[];
  context_sentence: string;
  used_fake_provider: boolean;
}

export async function classifyEntities(args: {
  server: string;
  clientId: string;
  accessCode: string;
  corpusFile: File;
  adapter: string;
  /** When the ingest adapter knows the parties (OFW cover page, etc.),
   *  pass them as hints so the classifier banner reads in the same
   *  order as the ingest banner above it. Ignored server-side if the
   *  hints don't match the top-2 senders. */
  hintPartyA?: string | null;
  hintPartyB?: string | null;
}): Promise<ClassifyEntitiesResponse> {
  const fd = new FormData();
  fd.append("corpus_csv", args.corpusFile, args.corpusFile.name);
  fd.append("adapter", args.adapter);
  if (args.hintPartyA) fd.append("hint_party_a", args.hintPartyA);
  if (args.hintPartyB) fd.append("hint_party_b", args.hintPartyB);
  const r = await fetch(serverUrl(args.server, "/runs/classify-entities"), {
    method: "POST",
    headers: authHeaders(args.clientId, args.accessCode),
    body: fd,
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "classify-entities failed",
    );
  }
  return r.json();
}


export async function fetchStatus(args: {
  server: string;
  runId: string;
  runToken: string;
}): Promise<StatusResponse> {
  const r = await fetch(
    serverUrl(args.server, `/runs/${args.runId}/status?token=${encodeURIComponent(args.runToken)}`),
  );
  if (!r.ok) throw makeApiError(r.status, await r.json().catch(() => ({})), "status failed");
  return r.json();
}

export async function cancelRun(args: {
  server: string;
  runId: string;
  runToken: string;
}): Promise<{ ok: boolean; alreadyTerminal: boolean }> {
  const r = await fetch(
    serverUrl(args.server, `/runs/${args.runId}/cancel?token=${encodeURIComponent(args.runToken)}`),
    { method: "POST" },
  );
  if (r.status === 409) return { ok: true, alreadyTerminal: true };
  if (!r.ok) throw makeApiError(r.status, await r.json().catch(() => ({})), "cancel failed");
  return { ok: true, alreadyTerminal: false };
}

/**
 * Download the encrypted single-file viewer.html for a completed run.
 *
 * The server returns the HTML body PLUS the AES-GCM decryption
 * passphrase in the `X-Aurora-Passphrase` response header. The caller
 * gets back both, displays the passphrase, and triggers a save of the
 * blob. See docs/topics/pro-se-cloud/viewer-bundle-interface.md.
 */
export async function downloadViewerBundle(args: {
  server: string;
  runId: string;
  runToken: string;
  label?: string;
}): Promise<{
  blob: Blob;
  passphrase: string;
  consumed: boolean;
}> {
  // format=zip: the survivor-facing delivery bundle — the encrypted viewer
  // wrapped with a plaintext READ ME FIRST.txt (server assembles it). The
  // passphrase headers below are unaffected by the body format.
  const params = new URLSearchParams({ token: args.runToken, format: "zip" });
  if (args.label) params.set("label", args.label);
  const r = await fetch(
    serverUrl(args.server, `/runs/${args.runId}/viewer?${params}`),
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "viewer download failed",
    );
  }
  // A re-download after the passphrase was already shown once and deleted
  // server-side: the encrypted file still comes back, but there's no key to
  // show again — the user keeps the passphrase they saved the first time.
  const consumed = r.headers.get("X-Aurora-Passphrase-Consumed") === "1";
  const passphrase = r.headers.get("X-Aurora-Passphrase") ?? "";
  if (!passphrase && !consumed) {
    throw new Error(
      "server returned the viewer bundle but no X-Aurora-Passphrase " +
        "header — check CORS expose_headers config",
    );
  }
  return { blob: await r.blob(), passphrase, consumed };
}

// Stripe billing scaffold — create a Checkout session for an access code +
// tier. Only meaningful when the server has AURORA_CLOUD_STRIPE_ENABLED=1
// (otherwise /billing/checkout 404s). The cost screen's "Authorize" action
// calls this and redirects to `url` when billing is on; under pilot-free it
// keeps launching the run directly.
//
// Idempotency: if the code is ALREADY paid the server mints no session and
// returns `{ alreadyPaid: true }` — the caller must launch the run rather than
// redirect, so a returning buyer is never charged twice.
export type CheckoutSession =
  | { url: string; id: string }
  | { clientSecret: string; id: string }
  | { alreadyPaid: true };

export async function createCheckoutSession(args: {
  server: string;
  accessCode: string;
  tierName: string;
  // Embedded mode (Option A): ask for a client_secret to mount Stripe's
  // checkout inside the wizard card instead of a redirect url. Only sent
  // true when /config carried a publishable key; the server's fake no-key
  // rail answers hosted-shaped (url) regardless, and the caller must
  // handle either shape.
  embedded?: boolean;
}): Promise<CheckoutSession> {
  const r = await fetch(serverUrl(args.server, "/billing/checkout"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      access_code: args.accessCode,
      tier_name: args.tierName,
      embedded: args.embedded === true,
      // Revenue attribution: the server stamps these onto the Stripe
      // session metadata (aurora_cloud/billing_routes.py).
      ...adClickFields(),
    }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "checkout session failed",
    );
  }
  const body = await r.json();
  if (body?.already_paid) return { alreadyPaid: true };
  if (typeof body?.client_secret === "string") {
    return { clientSecret: body.client_secret, id: body.id as string };
  }
  return { url: body.url as string, id: body.id as string };
}

// --- Entitlement poll (embedded checkout's paid-yet? check) ---
//
// Stripe's in-card onComplete fires a beat before our webhook lands, so the
// wizard polls this until {paid: true} before launching the run. Read-only:
// never mints a session (unlike re-POSTing /billing/checkout) and the server
// reconciles against Stripe directly on a lost webhook. The code rides the
// X-Access-Code header, never a URL.
//
// paid_tier (review R1, 2026-08-05): WHICH PWYC tier the payment bought —
// same string vocabulary as PrescanResponse.paid_tier, read from the same
// payment record. On the lost-webhook path this response is the frontend's
// ONLY tier knowledge (the prescan ran before the record existed), and the
// cost screen's per-tier viability gate keys the right ceiling on it.
// Optional/nullable: an older server omits it, a pre-S25 record carries no
// tier — the gate then fails closed to the Access ceiling.

export async function getEntitlement(args: {
  server: string;
  accessCode: string;
}): Promise<{ paid: boolean; paid_tier?: string | null }> {
  const r = await fetch(serverUrl(args.server, "/billing/entitlement"), {
    headers: new Headers({ "X-Access-Code": args.accessCode }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "entitlement check failed",
    );
  }
  return r.json();
}

// --- Public config (front-door door-switching + pricing hydration) ---
//
// Called by Landing.tsx on mount (door switching: OFF -> early-access
// posture, ON -> "Start now"/"Got a code?") and by the marketing site's
// pricing.html (progressive-enhancement hydration of the printed PWYC
// ladder). Public, no auth headers, cacheable (~60s) — a cheap read.

export interface ConfigPwycTier {
  name: "Solidarity" | "Standard" | "Access";
  price_usd: number;
  blurb: string;
}

export interface ConfigResponse {
  self_serve_open: boolean;
  pwyc_tiers: ConfigPwycTier[];
  // pk_ key for the embedded Payment step; null/absent → the wizard falls
  // back to the hosted-redirect checkout flow.
  stripe_publishable_key?: string | null;
}

export async function getConfig(server: string): Promise<ConfigResponse> {
  const r = await fetch(serverUrl(server, "/config"));
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "config fetch failed");
  }
  return r.json();
}

// --- Self-serve start (front-door "Start now" mint) ---
//
// Called by Landing.tsx's primary door when self_serve_open is true. Public,
// no auth headers, Turnstile-verified + per-IP rate-limited server-side.
// 404s (Response, not thrown) when the door isn't actually open — the caller
// should have already hidden this door from that state, but the server is
// the enforcement point, not the client's config read.

export interface SelfServeStartResponse {
  code: string;
}

export async function startSelfServe(
  server: string,
  turnstileToken: string | null,
): Promise<SelfServeStartResponse> {
  const r = await fetch(serverUrl(server, "/self-serve/start"), {
    method: "POST",
    headers: new Headers({ "Content-Type": "application/json" }),
    // Mint is an ad visitor's first server touch — attribution rides it
    // onto the code's operator-visible notes + the mint Discord ping.
    body: JSON.stringify({
      turnstile_token: turnstileToken,
      ...adClickFields(),
    }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "could not start — please try again",
    );
  }
  return r.json();
}

// --- Public access-code validation ---
//
// Called by Landing.tsx before any wizard state exists. Public, no
// auth headers. Server is rate-limited per-IP; expect 429 under abuse.

export type ValidateReason =
  | "unknown"
  | "used"
  | "revoked"
  | "expired"
  | "no_fresh_starts";

export interface ValidateAccessCodeResponse {
  valid: boolean;
  code_id: string | null;
  reason: ValidateReason | null;
}

export async function validateAccessCode(
  server: string,
  code: string,
): Promise<ValidateAccessCodeResponse> {
  const r = await fetch(serverUrl(server, "/access-codes/validate"), {
    method: "POST",
    headers: new Headers({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "validate failed",
    );
  }
  return r.json();
}


// --- Reopen a delivered result (2026-07-04) ---
//
// Called by Landing.tsx after a "used" verdict from /access-codes/validate,
// to check whether the delivered analysis is still inside its retention
// window and can be re-served from the door instead of the ready email.
// `available` is the sole authority on whether the result can be reopened;
// `days_left` is display-only. Own rate limiter server-side, separate from
// /validate's — see access_codes_routes.py.

export type ReopenReason = "not_delivered" | "expired" | "unknown" | "revoked";

export interface ReopenAccessCodeResponse {
  available: boolean;
  cloud_run_id: string | null;
  run_token: string | null;
  days_left: number | null;
  reason: ReopenReason | null;
}

export async function reopenAccessCode(
  server: string,
  code: string,
): Promise<ReopenAccessCodeResponse> {
  const r = await fetch(serverUrl(server, "/access-codes/reopen"), {
    method: "POST",
    headers: new Headers({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "reopen failed",
    );
  }
  return r.json();
}


// --- Agreement acceptance (self-serve clickwrap) ---
//
// Welcome calls this on "begin", before the wizard opens, to record the
// user's acceptance of the current agreement version for their code. The
// server derives the version + text hash itself; we send only the code.

export interface AcceptAgreementResponse {
  accepted: boolean;
  agreement_version: string;
}

export async function acceptAgreement(
  server: string,
  code: string,
): Promise<AcceptAgreementResponse> {
  const r = await fetch(serverUrl(server, "/agreement/accept"), {
    method: "POST",
    headers: new Headers({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "agreement accept failed",
    );
  }
  return r.json();
}


// --- Linked-email lookup ---
//
// Welcome page calls this after Landing accepts a code, to prefill
// "where should we send your packet?" with the email the applicant
// gave at apply-form time. Returns null when there's no link (test
// codes minted by hand) or when the code is no longer valid.

export interface LinkedEmailResponse {
  email: string | null;
}

export async function lookupLinkedEmail(
  server: string,
  code: string,
): Promise<LinkedEmailResponse> {
  const r = await fetch(serverUrl(server, "/access-codes/linked-email"), {
    method: "POST",
    headers: new Headers({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "linked-email lookup failed",
    );
  }
  return r.json();
}


// --- Public pilot application submission ---
//
// Called by ApplyForm. Public route, no auth headers. Server rate-limits
// per-IP (5/hr); a 429 indicates either the operator's IP is being abused
// or the user is double-clicking submit.

export type ApplyRole =
  | "pro_se"
  | "attorney"
  | "advocate"
  | "court_appointed"
  | "researcher"
  | "other";

export type ApplyCaseStatus =
  | "preparing"
  | "filed"
  | "hearing_scheduled"
  | "mid_proceeding"
  | "post_judgment"
  | "not_yet"
  | "other";

export type ApplyHearingWindow =
  | "lt_2w"
  | "2_6w"
  | "1_3m"
  | "gt_3m"
  | "none";

export type ApplyTimeSpan =
  | "lt_6m"
  | "6m_2y"
  | "2_5y"
  | "gt_5y"
  | "unsure";

export type ApplySource =
  | "ofw"
  | "civil_communicator"
  | "two_houses"
  | "talking_parents"
  | "appclose"
  | "imessage"
  | "other"
  | "unsure";

export type ApplySupportStatus = "yes" | "alone" | "partial";
export type ApplyActiveCaseStatus = "yes" | "soon" | "not_yet";

export interface ApplyBody {
  name: string;
  email: string;
  role: ApplyRole;
  state_or_country?: string | null;
  // The light request-access form sends only name/email/role/state +
  // free-form notes. These structured fields are optional — kept on
  // the type so a direct API caller (or a future richer form) can
  // still supply them.
  case_status?: ApplyCaseStatus | null;
  hearing_window?: ApplyHearingWindow | null;
  sources?: ApplySource[];
  time_span?: ApplyTimeSpan | null;
  how_heard?: string | null;
  free_form_notes?: string | null;
  support_status?: ApplySupportStatus | null;
  active_case_status?: ApplyActiveCaseStatus | null;
  firm_and_jurisdiction?: string | null;
  turnstile_token?: string | null;
  // Ad-click attribution (src/lib/adClick.ts) — which ad click, if any,
  // led to this application. First-party capture only; see the module
  // header there for the no-Google-JS rationale.
  ad_click_kind?: AdClickKind | null;
  ad_click_id?: string | null;
  ad_landed_at?: string | null;
  ad_click_src?: string | null;
}

export interface ApplyResponse {
  ok: boolean;
  application_id: string;
}

export async function submitApplication(
  server: string,
  body: ApplyBody,
): Promise<ApplyResponse> {
  const r = await fetch(serverUrl(server, "/apply"), {
    method: "POST",
    headers: new Headers({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "apply failed",
    );
  }
  return r.json();
}


// --- Operator surface ---
//
// Server-side gate is hybrid (Phase 5): a request passes if it
// carries either a matching X-Operator-Token OR a verified CF Access
// JWT (the browser auto-attaches the CF_Authorization cookie when
// behind CF Access). An empty-string token here is the JWT-mode
// sentinel — we skip the token header entirely so the request
// authenticates via the cookie alone.

function operatorHeaders(token: string): Headers {
  if (!token) return new Headers();
  return new Headers({ "X-Operator-Token": token });
}

function operatorJsonHeaders(token: string): Headers {
  const h = new Headers({ "Content-Type": "application/json" });
  if (token) h.set("X-Operator-Token", token);
  return h;
}

export interface OperatorApplication {
  id: string;
  email: string;
  role: string;
  state_or_country: string | null;
  free_form_notes: string | null;
  created_at: string;
  // "concierge" = operator set this aside to handle personally (off-platform).
  // A reversible set-aside, not a decision — it's still approvable/declinable.
  status: "pending" | "approved" | "declined" | "concierge";
  approved_at: string | null;
  access_code_id: string | null;
  // v2 fields — nullable on pre-v5 rows (existing pilot applications
  // that predate the v2 form). New rows always populate name + the
  // required-set; conditionals are role-scoped server-side.
  name?: string | null;
  case_status?: string | null;
  hearing_window?: string | null;
  time_span?: string | null;
  sources?: string[];
  support_status?: string | null;
  active_case_status?: string | null;
  concierge_candidate?: boolean;
  details?: { how_heard?: string; firm_and_jurisdiction?: string };
}

export interface OperatorAccessCode {
  id: string;
  code: string;
  created_at: string;
  expires_at: string | null;
  used_at: string | null;
  application_id: string | null;
  // Server denormalizes the applicant email so the operator table can
  // show a human-readable column instead of an opaque application_id
  // UUID. Null for test codes minted without an application link.
  applicant_email: string | null;
  // Applicant display name (denormalized like applicant_email). Null for
  // hand-minted/test codes with no application link.
  applicant_name: string | null;
  // Notify address attached at mint (hand-minted codes). Null for codes that
  // resolve their address via the linked application instead.
  email: string | null;
  status: "active" | "used" | "revoked";
  // Grant-funded (free on the pilot key) vs paid-required. Server
  // denormalizes this so the table can show a comp/paid badge.
  comp: boolean;
  // The clickwrap acceptance stamp for this code — the "did this person
  // agree?" readout. Null when no acceptance is on file (pre-clickwrap
  // codes, or a code that never reached the agreement).
  agreement: { version: string; accepted_at: string } | null;
}

export interface OperatorRunRecord {
  id: string;
  cloud_run_id: string;
  access_code_id: string | null;
  // Server denormalizes the access code string into list/detail/receipt
  // responses so the operator table doesn't have to N+1 the access-codes
  // endpoint per row. Null when the run was launched without an access
  // code (legacy / smoke runs) or the linked code has been deleted.
  access_code: string | null;
  // Server-denormalized owner (run -> code -> application; the attached mint
  // email wins, else the linked applicant). owner_name is null for hand-minted
  // codes with no application behind them. Operator-only attribution.
  owner_email: string | null;
  owner_name: string | null;
  // v16: the run's notify-email override (POST /runs/notify-email), mirrored
  // onto the run_records row. Fallback OWNER source for a self-serve run —
  // one with no application behind its access code, so owner_email/
  // owner_name resolve nothing. Absent on older servers.
  notify_email?: string | null;
  started_at: string;
  completed_at: string | null;
  status: string;
  cost_usd_anthropic: number | null;
  // Set on the display projection when consumed preview(s) folded into this
  // row: the rolled-up discovery cost already INCLUDED in cost_usd_anthropic.
  // Lets the UI annotate "incl. preview $X" + mark the journey. Absent on
  // older servers / raw reads.
  journey_preview_cost_usd?: number | null;
  // preview | full | offramp (v8 servers; absent before).
  kind?: string | null;
  notes: string | null;
}

async function operatorGet<T>(server: string, path: string, token: string): Promise<T> {
  // credentials: "include" so the CF Access cookie rides the request and the
  // server's hybrid gate can authenticate via JWT in token-less ("") JWT mode —
  // matches the OperatorShell probe + the SSE EventSource(withCredentials). Same
  // origin today (cookie would ride anyway), so this is symmetry + future-proofing
  // against a cross-origin operator base.
  const r = await fetch(serverUrl(server, path), {
    headers: operatorHeaders(token),
    credentials: "include",
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      `${path} failed`,
    );
  }
  return r.json() as Promise<T>;
}

export function operatorListApplications(
  server: string,
  token: string,
): Promise<{ applications: OperatorApplication[] }> {
  return operatorGet(server, "/operator/applications", token);
}

export function operatorGetApplication(
  server: string,
  token: string,
  id: string,
): Promise<OperatorApplication> {
  return operatorGet(server, `/operator/applications/${encodeURIComponent(id)}`, token);
}

export function operatorListAccessCodes(
  server: string,
  token: string,
): Promise<{ access_codes: OperatorAccessCode[] }> {
  return operatorGet(server, "/operator/access-codes", token);
}

export function operatorListRuns(
  server: string,
  token: string,
): Promise<{ runs: OperatorRunRecord[] }> {
  return operatorGet(server, "/operator/runs", token);
}

export interface OperatorPersonRun {
  cloud_run_id: string;
  status: string;
  kind: string | null;
  started_at: string | null;
  completed_at: string | null;
  cost_usd_anthropic: number | null;
}

export interface OperatorPersonCode {
  id: string;
  code: string;
  status: string;
  comp: boolean;
  created_at: string | null;
}

// One event in a person's activity timeline. `category` drives the filter
// chips (code · run · payment · email · download). Metadata only — never
// case content. `detail` is a free-form structural bag (code, amount, counts).
export interface OperatorActivityEvent {
  ts: string;
  category: "code" | "run" | "payment" | "email" | "download";
  type: string;
  actor: string | null;
  summary: string;
  detail: Record<string, unknown>;
}

export interface OperatorPersonActivity {
  person: {
    email: string | null;
    name: string | null;
    role: string | null;
    application_id: string | null;
    codes: string[];
    code_count: number;
    run_count: number;
  } | null;
  events: OperatorActivityEvent[];
}

// One row per human, projected server-side by grouping applications + access
// codes + runs on the owner email (there is no canonical person table). `stage`
// is the derived lifecycle position; see the server's people.py.
export interface OperatorPerson {
  email: string;
  name: string | null;
  role: string | null;
  stage: string;
  application_id: string | null;
  code: string | null;
  code_count: number;
  run_count: number;
  last_activity: string | null;
  runs: OperatorPersonRun[];
  codes: OperatorPersonCode[];
}

export function operatorListPeople(
  server: string,
  token: string,
): Promise<{ people: OperatorPerson[] }> {
  return operatorGet(server, "/operator/people", token);
}

// Re-access a run's ENCRYPTED result bundle through the operator gate (the
// timeline's "download link"). Fetches with the operator auth (header + CF
// cookie), then triggers a browser download of the ciphertext. Throws on 404
// (bundle purged / never existed) so the caller can surface "no longer
// available". Never carries the passphrase or run token.
export async function operatorDownloadRunBundle(
  server: string,
  token: string,
  bundleUrl: string,
  cloudRunId: string,
): Promise<void> {
  const r = await fetch(serverUrl(server, bundleUrl), {
    headers: operatorHeaders(token),
    credentials: "include",
  });
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "download failed");
  }
  const blob = await r.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `aurora-encrypted-${cloudRunId}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

// The per-person activity timeline. Identify the person by an OPAQUE id — a
// code id or an application id — never the email or the code string in the URL.
export function operatorPersonActivity(
  server: string,
  token: string,
  handle: { codeId?: string | null; applicationId?: string | null },
): Promise<OperatorPersonActivity> {
  const params = new URLSearchParams();
  if (handle.applicationId) params.set("application_id", handle.applicationId);
  if (handle.codeId) params.set("code_id", handle.codeId);
  return operatorGet(
    server,
    `/operator/people/activity?${params.toString()}`,
    token,
  );
}

// Live status for one in-flight run. Operator-authed mirror of the
// token-authed /runs/{id}/status — the operator never holds the run token,
// so this carries the operator gate instead (and the server omits the
// customer's pending passphrase on this surface). Polled by the Live runs
// view; the X-Operator-Token header authenticates every poll, which an
// EventSource can't carry — so the SSE event tail
// (/operator/runs/{id}/events, CF-cookie auth) is a separate enhancement.
export function operatorRunStatus(
  server: string,
  token: string,
  cloudRunId: string,
): Promise<StatusResponse> {
  return operatorGet(
    server,
    `/operator/runs/${encodeURIComponent(cloudRunId)}/status`,
    token,
  );
}

export function operatorGetRun(
  server: string,
  token: string,
  id: string,
): Promise<OperatorRunRecord> {
  return operatorGet(server, `/operator/runs/${encodeURIComponent(id)}`, token);
}

export function operatorGetRunReceipt(
  server: string,
  token: string,
  id: string,
): Promise<OperatorRunRecord> {
  // Receipt endpoint returns the same OperatorRunRecord shape today.
  // It exists as a separate endpoint so a future end-user-facing
  // receipt can diverge from the operator's dashboard view without
  // breaking either contract.
  return operatorGet(
    server,
    `/operator/runs/${encodeURIComponent(id)}/receipt`,
    token,
  );
}

export async function operatorCancelRun(
  server: string,
  token: string,
  cloudRunId: string,
): Promise<{ cancelled_at: string }> {
  // Operator kill-switch: same termination core as the user's token-authed
  // cancel, authenticated by operator credentials instead (the operator
  // endpoints never expose run tokens). 409 = already terminal.
  const r = await fetch(
    serverUrl(server, `/operator/runs/${encodeURIComponent(cloudRunId)}/cancel`),
    { method: "POST", headers: operatorHeaders(token) },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "cancel failed",
    );
  }
  return r.json() as Promise<{ cancelled_at: string }>;
}

// Pilot-meta analytical cuts (Phase C) — the same shapes the research
// registry derives offline (off-ramp exceedance, pricing by size/config,
// words/msg anomaly), computed live server-side from content-free
// run_records so the operator can read them in /operator without the pull.
export interface OperatorMetrics {
  summary: {
    total: number;
    completed: number;
    off_ramped: number;
    wpm_median: number | null;
  };
  offramps: {
    run_id: string;
    tier: string | null;
    message_count: number | null;
    estimate_usd: number | null;
    budget_usd: number | null;
    exceedance_ratio: number | null;
    words_per_msg_mean: number | null;
  }[];
  pricing_by_size: {
    size_band: string;
    runs: number;
    mean_cost: number;
    min_cost: number;
    max_cost: number;
  }[];
  pricing_by_config: {
    model: string;
    prompt_version: string;
    runs: number;
    mean_cost: number;
    min_cost: number;
    max_cost: number;
    cost_per_1k_msgs: number | null;
  }[];
  anomalies: {
    run_id: string;
    tier: string | null;
    message_count: number | null;
    words_per_msg_mean: number | null;
    words_per_msg_p95: number | null;
    words_per_msg_max: number | null;
    outlier: boolean;
  }[];
}

export function operatorGetMetrics(
  server: string,
  token: string,
): Promise<OperatorMetrics> {
  return operatorGet(server, "/operator/runs/metrics", token);
}


export interface OperatorFeedback {
  id: string;
  submitted_at: string;
  access_code_id: string | null;
  access_code: string | null;  // denormalized by the server
  email: string | null;
  body: string;
  status: "new" | "reviewed" | "archived";
}

// The 1–5 helpfulness rating left from the post-run email's rating link.
// One per run (cloud_run_id is the key); a re-submit overwrites.
export interface OperatorFeedbackRating {
  cloud_run_id: string;
  score: number;
  comment: string | null;
  access_code: string | null;  // denormalized by the server
  created_at: string;
  updated_at: string;
  reviewed: boolean;           // reversible review marker
}

export function operatorListFeedback(
  server: string,
  token: string,
): Promise<{ feedback: OperatorFeedback[]; ratings: OperatorFeedbackRating[] }> {
  return operatorGet(server, "/operator/feedback", token);
}

export async function operatorUpdateFeedbackStatus(
  server: string,
  token: string,
  id: string,
  status: "new" | "reviewed" | "archived",
): Promise<OperatorFeedback> {
  const r = await fetch(
    serverUrl(server, `/operator/feedback/${encodeURIComponent(id)}/status`),
    {
      method: "POST",
      headers: operatorJsonHeaders(token),
      body: JSON.stringify({ status }),
    },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "feedback status update failed",
    );
  }
  return r.json();
}

export async function operatorSetRatingReviewed(
  server: string,
  token: string,
  cloudRunId: string,
  reviewed: boolean,
): Promise<{ cloud_run_id: string; reviewed: boolean }> {
  const r = await fetch(
    serverUrl(server, "/operator/feedback/rating-review"),
    {
      method: "POST",
      headers: operatorJsonHeaders(token),
      body: JSON.stringify({ cloud_run_id: cloudRunId, reviewed }),
    },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "rating review update failed",
    );
  }
  return r.json();
}


// One answer to the run screen's optional "where did you hear about
// Aurora?" question, flattened server-side from its wizard-event audit row.
// `source` is a chip slug (facebook, referral, …) — null only on a row
// something other than the wizard wrote.
export interface OperatorHowHeard {
  recorded_at: string;
  source: string | null;
  detail: string | null;
  access_code: string | null; // denormalized by the server
  entered_by: string | null; // "operator" for a hand-logged row, else null
  name: string | null; // who the referral is about (hand-logged rows)
}

export function operatorListHowHeard(
  server: string,
  token: string,
): Promise<{ answers: OperatorHowHeard[] }> {
  return operatorGet(server, "/operator/how-heard", token);
}

// Hand-log a how-heard answer for a lead who never ran the wizard (an email
// or phone referral). The server writes it into the same rollup as run-screen
// answers, marked entered_by="operator".
export async function operatorAddHowHeard(
  server: string,
  token: string,
  body: {
    source: string;
    detail?: string | null;
    name?: string | null;
    access_code?: string | null;
  },
): Promise<{ ok: boolean; source: string; detail: string | null; name: string | null }> {
  const r = await fetch(serverUrl(server, "/operator/how-heard"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "could not log referral",
    );
  }
  return r.json();
}


export interface MintAccessCodesBody {
  count?: number;
  prefix?: string;
  expires_in_days?: number | null;
  notes?: string | null;
  // Optional notify address attached to the minted code(s) so the full
  // ready/nudge/feedback email cycle resolves an address without an
  // application behind the code.
  email?: string | null;
  // Comp = grant-funded: the code runs free on the pilot key, no Stripe
  // payment and no personal key required. Unset/false → a paid-required
  // code (user pays per analysis with their own key). Defaults false.
  comp?: boolean;
}

export async function operatorMintAccessCodes(
  server: string,
  token: string,
  body: MintAccessCodesBody,
): Promise<{ access_codes: OperatorAccessCode[] }> {
  const r = await fetch(serverUrl(server, "/operator/access-codes"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "mint access codes failed",
    );
  }
  return r.json();
}

export async function operatorRevokeAccessCode(
  server: string,
  token: string,
  id: string,
): Promise<OperatorAccessCode> {
  const r = await fetch(
    serverUrl(server, `/operator/access-codes/${encodeURIComponent(id)}/revoke`),
    {
      method: "POST",
      headers: operatorHeaders(token),
    },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "revoke access code failed",
    );
  }
  return r.json();
}

export async function operatorResendAccessCodeEmail(
  server: string,
  token: string,
  id: string,
): Promise<{ access_code: OperatorAccessCode; email_status: EmailStatus }> {
  const r = await fetch(
    serverUrl(
      server,
      `/operator/access-codes/${encodeURIComponent(id)}/resend-email`,
    ),
    {
      method: "POST",
      headers: operatorHeaders(token),
    },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "resend code email failed",
    );
  }
  return r.json();
}

export async function operatorSetAccessCodeComp(
  server: string,
  token: string,
  id: string,
  comp: boolean,
): Promise<OperatorAccessCode> {
  const r = await fetch(
    serverUrl(server, `/operator/access-codes/${encodeURIComponent(id)}/comp`),
    {
      method: "POST",
      headers: operatorJsonHeaders(token),
      body: JSON.stringify({ comp }),
    },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "set access code billing failed",
    );
  }
  return r.json();
}


export interface EmailStatus {
  ok: boolean;
  id: string | null;
  error: string | null;
  // True when the server is in fake-email mode (or has no Resend key): `ok` is
  // still true but nothing was actually delivered. Optional for back-compat with
  // the no-op (already-approved / race-lost) branches that don't set it.
  faked?: boolean;
  // True on an idempotent replay (already-approved / race-lost): no code was
  // minted and no email attempted — distinct from a real send failure.
  noop?: boolean;
}

export interface ApproveApplicationResponse {
  application: OperatorApplication;
  // null when an idempotent replay finds the linked code was deleted
  // out-of-band, or when revoking an orphan after a lost race.
  access_code: OperatorAccessCode | null;
  email_status: EmailStatus;
}

export async function operatorApproveApplication(
  server: string,
  token: string,
  appId: string,
  comp: boolean = false,
): Promise<ApproveApplicationResponse> {
  const r = await fetch(
    serverUrl(server, `/operator/applications/${encodeURIComponent(appId)}/approve`),
    {
      method: "POST",
      headers: operatorJsonHeaders(token),
      body: JSON.stringify({ comp }),
    },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "approve application failed",
    );
  }
  return r.json();
}


export async function operatorDeclineApplication(
  server: string,
  token: string,
  appId: string,
): Promise<{ application: OperatorApplication }> {
  const r = await fetch(
    serverUrl(server, `/operator/applications/${encodeURIComponent(appId)}/decline`),
    { method: "POST", headers: operatorHeaders(token) },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "decline application failed",
    );
  }
  return r.json();
}


// Set aside an application as "concierge" (operator handling it personally),
// or return it to the pending queue (on=false). No email, no code minted —
// the application stays approvable/declinable.
export async function operatorSetApplicationConcierge(
  server: string,
  token: string,
  appId: string,
  on: boolean,
): Promise<{ application: OperatorApplication }> {
  const r = await fetch(
    serverUrl(server, `/operator/applications/${encodeURIComponent(appId)}/concierge`),
    {
      method: "POST",
      headers: operatorJsonHeaders(token),
      body: JSON.stringify({ on }),
    },
  );
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "set concierge failed",
    );
  }
  return r.json();
}


// --- Audit log ---


export interface OperatorAuditEntry {
  id: number;
  recorded_at: string;          // ISO-8601 UTC
  actor: string;                // CF Access email, or 'unknown'
  action: string;               // e.g. 'approve_application'
  target_kind: string;          // 'application' | 'access_code' | 'feedback' | 'codes_batch'
  target_id: string;
  // JSON blob shape varies per action — kept opaque on the wire; the
  // UI renders it as a collapsed JSON pre. Server hands back parsed
  // objects when the row's details json is valid; null when absent.
  details: Record<string, unknown> | null;
}

export interface OperatorAuditLogResponse {
  entries: OperatorAuditEntry[];
  limit: number;
  offset: number;
}

export function operatorListAuditLog(
  server: string,
  token: string,
  opts?: { limit?: number; offset?: number },
): Promise<OperatorAuditLogResponse> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  const path = qs ? `/operator/audit-log?${qs}` : "/operator/audit-log";
  return operatorGet(server, path, token);
}


// SSE event subscription. EventSource can't send custom headers, so the
// per-run token goes in the query string (same as the demo HTML
// harness — server expects either run_token or preview_token there).
// Returns a cleanup function.
export type SseEvent = { type: string; data: unknown };

const SSE_EVENT_TYPES = [
  "chain_spawned",
  "progress",
  "stage_done",
  "fanout_progress",
  "pattern_progress",
  // The fan-out's weak-pattern guard naming the discovered patterns that will
  // not reach the packet — the results card subtracts them (2026-09-08).
  "patterns_dropped",
  "audit_progress",
  "synthesis_progress",
  "preview_ready",
  "cost_estimate",
  "error",
  "complete",
  "cancelled",
  "terminal_state",
];

type SseHandlers = {
  onEvent: (ev: SseEvent) => void;
  onError?: (msg: string) => void;
  onTerminal?: (data: { chain_state: string; exit_code?: number | null }) => void;
};

function attachSseHandlers(es: EventSource, args: SseHandlers): () => void {
  for (const t of SSE_EVENT_TYPES) {
    es.addEventListener(t, (raw) => {
      let data: unknown;
      try {
        data = JSON.parse((raw as MessageEvent).data);
      } catch {
        data = (raw as MessageEvent).data;
      }
      args.onEvent({ type: t, data });
      if (t === "terminal_state") {
        es.close();
        if (args.onTerminal && data && typeof data === "object") {
          args.onTerminal(data as { chain_state: string; exit_code?: number });
        }
      }
    });
  }
  es.onerror = () => {
    // EventSource auto-reconnects on transient errors; only inform on
    // hard failure (readyState === CLOSED).
    if (es.readyState === EventSource.CLOSED && args.onError) {
      args.onError("SSE connection closed");
    }
  };
  return () => es.close();
}

export function subscribeEvents(args: {
  server: string;
  runId: string;
  runToken: string;
} & SseHandlers): () => void {
  const url = serverUrl(
    args.server,
    `/runs/${args.runId}/events?token=${encodeURIComponent(args.runToken)}`,
  );
  return attachSseHandlers(new EventSource(url), args);
}

// Operator-authed SSE. Crucially, NO token in the URL — the high-value
// operator token must never ride in a query string (logs/history/referrer).
// Auth is the Cloudflare Access cookie: the edge validates it and injects the
// verified JWT header the origin gate reads, so EventSource authenticates in
// production (behind Access). On localhost / non-CF dev there's no cookie and
// EventSource can't send X-Operator-Token, so the stream won't open — callers
// MUST degrade gracefully (the Live runs view falls back to /status polling).
export function subscribeOperatorEvents(args: {
  server: string;
  runId: string;
} & SseHandlers): () => void {
  const url = serverUrl(
    args.server,
    `/operator/runs/${encodeURIComponent(args.runId)}/events`,
  );
  return attachSseHandlers(new EventSource(url, { withCredentials: true }), args);
}


// --- Runtime settings: pilot_free / fake-provider toggles + pilot key ---
//
// Every settings call returns the full server-authoritative snapshot
// (OperatorSettings) so the panel re-renders from the same shape whether
// it just read, flipped a flag, or set the key.

export interface OperatorFlagState {
  value: boolean;
  // "override" → an operator set it; "env" → falling back to the value
  // baked into the container at deploy time.
  source: "override" | "env";
  env_default: boolean;
}

export interface OperatorKeyState {
  set: boolean;
  masked: string | null; // e.g. "sk-ant-a…3456"; never the full key
}

export interface OperatorBillingState {
  // The key a real run would charge right now.
  active_source: "pilot" | "personal";
  active_key_set: boolean;
  // True when a real run can't launch because the selected key slot is
  // empty (live + pilot_free with no pilot key).
  billable_runs_blocked: boolean;
  // Whether Stripe is wired at the env level (enabled + webhook secret). The
  // operator UI keeps the public-checkout toggle hidden until this is true —
  // there's nothing to gate before checkout can even exist — and shows a
  // read-only "not available yet" line instead. Read-only; no gate semantics.
  stripe_configured: boolean;
  // The compound checkout gate: whether a public self-serve checkout can
  // actually start right now (public_access AND stripe_configured AND not-fake).
  // The raw public_access flag is not the whole truth.
  self_serve_open: boolean;
}

// A runtime-overridable numeric cap. value/env_default may be null (an unset
// budget ceiling = no off-ramp). source says whether the live value is the
// operator override or the env/bootstrap default.
export interface OperatorLimitState {
  value: number | null;
  source: "override" | "env";
  env_default: number | null;
}

export type OperatorLimitName =
  | "anthropic_budget_usd"
  | "anthropic_budget_usd_access"
  | "max_upload_bytes"
  | "prescan_rate_limit_per_hour"
  | "feedback_email_delay_hours"
  | "daily_preview_cap_usd"
  | "daily_conversion_cap_usd"
  | "daily_classify_cap_usd";

// A free-text setting (email addresses). value/env_default may be null.
export interface OperatorStringState {
  value: string | null;
  source: "override" | "env";
  env_default: string | null;
}

export type OperatorFanoutMode = "auto" | "batch" | "sync";

// A constrained-string (enum) runtime setting. fanout_mode picks the fan-out
// transport; options + auto_sync_max_tokens are operator-visible context.
export interface OperatorFanoutModeState {
  value: OperatorFanoutMode;
  source: "override" | "env";
  env_default: OperatorFanoutMode;
  options: OperatorFanoutMode[];
  auto_sync_max_tokens: number;
  // True only on staging — whether the operator may force batch/sync. Prod
  // leaves this off: fan-out is always auto and the console hides the selector.
  force_allowed: boolean;
}

export type OperatorEnumName = "fanout_mode";

export type OperatorEmailTemplateName =
  | "feedback"
  | "ready"
  | "nudge"
  | "run_delayed"
  | "run_delayed_followup"
  | "run_delayed_final"
  | "run_failed"
  | "apply"
  | "approval"
  | "code_expiry";

export interface OperatorEmailTemplate {
  subject: string; // effective (override or default)
  body: string;
  overridden: boolean;
  default_subject: string;
  default_body: string;
  tokens: string[]; // {{token}} names this template accepts
}

export interface OperatorEmailSettings {
  from_address: OperatorStringState;
  reply_to_address: OperatorStringState;
  resend_key_set: boolean; // the Resend secret lives in the VPS .env, not here
  use_fake_email: OperatorFlagState;
  feedback_email_enabled: OperatorFlagState;
  ready_email_enabled: OperatorFlagState;
  feedback_email_delay_hours: OperatorLimitState;
  templates: Record<OperatorEmailTemplateName, OperatorEmailTemplate>;
}

export interface OperatorSettings {
  // When set, fake provider + fake email are hard-latched on server-side and
  // the flag/mode routes refuse to flip them (409). No box sets the lock today
  // (staging is safe-default-fake but operator-flippable), so normally false.
  fake_mode_locked?: boolean;
  use_fake_provider: OperatorFlagState;
  pilot_free: OperatorFlagState;
  // The public self-serve checkout kill-switch. The raw flag; billing
  // .self_serve_open is the effective truth (compounded with stripe config).
  public_access: OperatorFlagState;
  // Operator-set provider-outage signal → drives the user-facing degraded
  // banner + forces the keep-informed emails' outage line on.
  anthropic_service_degraded: OperatorFlagState;
  // Fan-out transport: auto (size-based), batch / sync (force either).
  fanout_mode: OperatorFanoutModeState;
  pilot_key: OperatorKeyState;
  personal_key: OperatorKeyState;
  billing: OperatorBillingState;
  limits: Record<OperatorLimitName, OperatorLimitState>;
  email: OperatorEmailSettings;
}

export type OperatorFlagName =
  | "pilot_free"
  | "use_fake_provider"
  | "use_fake_email"
  | "feedback_email_enabled"
  | "ready_email_enabled"
  | "anthropic_service_degraded"
  | "public_access";

export type OperatorEmailAddressName =
  | "email_from_address"
  | "email_reply_to_address";

export function operatorGetSettings(
  server: string,
  token: string,
): Promise<OperatorSettings> {
  return operatorGet(server, "/operator/settings", token);
}

export async function operatorSetFlag(
  server: string,
  token: string,
  name: OperatorFlagName,
  value: boolean | null, // null clears the override (revert to env)
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/flags"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ name, value }),
  });
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "set flag failed");
  }
  return r.json();
}

// Set (number) or clear (null) a runtime numeric cap. Applies without a
// restart; null reverts to the env/bootstrap default.
export async function operatorSetLimit(
  server: string,
  token: string,
  name: OperatorLimitName,
  value: number | null,
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/limits"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ name, value }),
  });
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "set limit failed");
  }
  return r.json();
}

// Set (one of the allowed values) or clear (null) a constrained-string runtime
// setting. Applies without a restart; null reverts to the env/bootstrap default.
export async function operatorSetEnum(
  server: string,
  token: string,
  name: OperatorEnumName,
  value: string | null,
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/enums"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ name, value }),
  });
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "set fan-out mode failed");
  }
  return r.json();
}

// --- tier pricing (operator-editable prices for the purchasable tiers) ---

export interface OperatorTierPrice {
  name: string; // PWYC ladder: Solidarity | Standard | Access
  code_default: number; // the built-in price in prescan.PWYC_TIERS
  override: number | null; // operator override, or null when unset
  effective: number; // what checkout actually charges (override ?? code_default)
  recommended: number | null; // legacy margin-tool rec (null under PWYC)
  status: string | null; // legacy margin status (null under PWYC)
  // Server-side context line (e.g. Access: "cross-subsidized by Solidarity —
  // below cost by design"). Optional: an older server omits it.
  note?: string | null;
}

export interface OperatorPricing {
  tiers: OperatorTierPrice[];
  min: number;
  max: number;
}

export function operatorGetPricing(
  server: string,
  token: string,
): Promise<OperatorPricing> {
  return operatorGet(server, "/operator/pricing", token);
}

// Set (whole-dollar int) or clear (null → revert to the code default) one
// tier's price. Applies to the next checkout — no redeploy.
export async function operatorSetTierPrice(
  server: string,
  token: string,
  name: string,
  value: number | null,
): Promise<OperatorPricing> {
  const r = await fetch(serverUrl(server, "/operator/pricing"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ name, value }),
  });
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "set tier price failed");
  }
  return r.json();
}

export type RunModeName = "demo" | "pilot" | "live";

// Atomic run-mode switch — sets the two underlying flags server-side and
// records ONE legible audit row (set_run_mode → <mode>) instead of two
// raw flag toggles.
export async function operatorSetMode(
  server: string,
  token: string,
  mode: RunModeName,
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/mode"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ mode }),
  });
  if (!r.ok) {
    throw makeApiError(r.status, await r.json().catch(() => ({})), "set mode failed");
  }
  return r.json();
}

export async function operatorSetPilotKey(
  server: string,
  token: string,
  key: string,
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/pilot-key"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ key }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "set pilot key failed",
    );
  }
  return r.json();
}

export async function operatorClearPilotKey(
  server: string,
  token: string,
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/pilot-key"), {
    method: "DELETE",
    headers: operatorHeaders(token),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "clear pilot key failed",
    );
  }
  return r.json();
}

// Set (non-empty) or clear (null) an email from/reply address. null reverts to
// the env default. The server validates it looks like an address (422 if not).
export async function operatorSetEmailAddress(
  server: string,
  token: string,
  name: OperatorEmailAddressName,
  value: string | null,
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/email-address"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ name, value }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "set email address failed",
    );
  }
  return r.json();
}

// Set (subject + body) or reset (both null) a transactional email template.
export async function operatorSetEmailTemplate(
  server: string,
  token: string,
  name: OperatorEmailTemplateName,
  subject: string | null,
  body: string | null,
): Promise<OperatorSettings> {
  const r = await fetch(serverUrl(server, "/operator/settings/email-template"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ name, subject, body }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "set email template failed",
    );
  }
  return r.json();
}

export interface OperatorEmailTestResult {
  ok: boolean;
  error: string | null;
  faked: boolean; // true when fake-email mode (or no Resend key) → nothing actually sent
  settings: OperatorSettings;
}

// Render the named template with sample values and send it to `to` — respects
// the current fake-email mode (faked=true means it was logged, not sent).
export async function operatorSendTestEmail(
  server: string,
  token: string,
  template: OperatorEmailTemplateName,
  to: string,
): Promise<OperatorEmailTestResult> {
  const r = await fetch(serverUrl(server, "/operator/settings/email-test"), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    body: JSON.stringify({ template, to }),
  });
  if (!r.ok) {
    throw makeApiError(
      r.status,
      await r.json().catch(() => ({})),
      "send test email failed",
    );
  }
  return r.json();
}


// --- Portal client console (SFTPGo-backed client portal) ---
//
// Metadata roster for the operator's onboard/invite/deliver workflow. The
// portal itself (upload/download) is SFTPGo's built-in web client — this is
// just the operator-side control plane. The client's own password is never
// retrievable; the one bounded exception (Rich, 2026-07-13) is `temp_code`
// below — the outstanding one-time code, kept server-side only until used.

export interface PortalClient {
  username: string;
  label: string;
  // Locked-account-at-onboard: "locked" = account created at onboard but disabled
  // until the client signs (awaiting signature); "provisioned" = signed + enabled;
  // "archived" = disabled + case archived. "invited"/"signed" are legacy states.
  status: "invited" | "signed" | "locked" | "provisioned" | "archived";
  provisioned_at: string | null;
  uploads_count: number;
  invite_sent_at: string | null;
  // Login/password progress from SFTPGo (provisioned clients only). last_login is
  // a Unix ms timestamp, or null if they've never signed in. password_set flips
  // true once they complete the forced password change.
  last_login: number | null;
  password_set: boolean;
  // The agreement link for an UNSIGNED (locked) client, so the operator can resend
  // it if the client lost it; null once signed.
  agreement_url: string | null;
  // The client's email on file (the onboarding form's email). null if none was
  // given — shown on the roster so the operator can see who they're relaying to.
  client_email: string | null;
  // The outstanding one-time login code, if one is un-used: issued at onboard/
  // invite/New code, cleared server-side once the client signs in (after issue)
  // or sets their password, or on rotate/archive. Lets the operator re-show a
  // code they haven't relayed yet instead of rotating it. Rendered masked with
  // an explicit Reveal.
  temp_code: string | null;
}

export function operatorListPortalClients(
  server: string,
  token: string,
): Promise<{ clients: PortalClient[] }> {
  return operatorGet(server, "/operator/portal/clients", token);
}

async function operatorPortalPost<T>(
  server: string,
  token: string,
  path: string,
  body: object | null,
): Promise<T> {
  const r = await fetch(serverUrl(server, path), {
    method: "POST",
    headers: operatorJsonHeaders(token),
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw makeApiError(r.status, await r.json().catch(() => ({})), `${path} failed`);
  return r.json() as Promise<T>;
}

// Locked-account-at-onboard: onboarding creates the account DISABLED and returns
// the one-time login code (temp_password, show-once) + the agreement link; if an
// email is on file the server auto-emails the agreement link (email_status reports
// the send; null when no email was given). Signing enables the account.
export function operatorOnboardPortalClient(
  server: string,
  token: string,
  username: string,
  label: string,
  email: string,
): Promise<{
  username: string;
  status: string;
  agreement_url?: string;
  temp_password?: string;
  email_status?: EmailStatus | null;
}> {
  return operatorPortalPost(server, token, "/operator/portal/clients", {
    username,
    label,
    email: email || null,
  });
}

// Manual ENABLE backstop — unlocks a client when signing didn't (enable-on-ack
// failed, or the agreement link lapsed and consent was verified out-of-band).
export function operatorProvisionPortalClient(
  server: string,
  token: string,
  username: string,
): Promise<{ username: string; status: string }> {
  return operatorPortalPost(
    server,
    token,
    `/operator/portal/clients/${encodeURIComponent(username)}/provision`,
    null,
  );
}

// Archive: disable the client's login, keep the encrypted data + case. Reversible.
export function operatorArchivePortalClient(
  server: string,
  token: string,
  username: string,
): Promise<{ username: string; status: string }> {
  return operatorPortalPost(
    server,
    token,
    `/operator/portal/clients/${encodeURIComponent(username)}/archive`,
    null,
  );
}

// Restore an archived client. Respects consent server-side: a signed client is
// re-enabled; an unsigned one is only un-archived (stays locked).
export function operatorRestorePortalClient(
  server: string,
  token: string,
  username: string,
): Promise<{ username: string; status: string }> {
  return operatorPortalPost(
    server,
    token,
    `/operator/portal/clients/${encodeURIComponent(username)}/restore`,
    null,
  );
}

// Resend the agreement link to a locked client — refreshes the 90-day window and
// re-emails the link. Server 409s if the client already signed.
export function operatorResendPortalAgreement(
  server: string,
  token: string,
  username: string,
): Promise<{ email_status: EmailStatus | null }> {
  return operatorPortalPost(
    server,
    token,
    `/operator/portal/clients/${encodeURIComponent(username)}/resend-agreement`,
    null,
  );
}

export function operatorSendPortalInvite(
  server: string,
  token: string,
  username: string,
): Promise<{ email_status: { ok: boolean }; temp_password: string;
             agreement_url?: string | null }> {
  return operatorPortalPost(
    server,
    token,
    `/operator/portal/clients/${encodeURIComponent(username)}/invite`,
    null,
  );
}

// Rotate a fresh one-time login code WITHOUT emailing — the relay-it-yourself path.
// Unlike operatorSendPortalInvite (/invite), this never requires an email on file, so
// "New code" works for an email-less client. Returns the temp once (server no-stores it).
export function operatorRotatePortalCode(
  server: string,
  token: string,
  username: string,
): Promise<{ temp_password: string; agreement_url?: string | null }> {
  return operatorPortalPost(
    server,
    token,
    `/operator/portal/clients/${encodeURIComponent(username)}/reset`,
    null,
  );
}

